// absolute imports
import { createTheme } from '@mui/material/styles';
import Ajv from 'ajv';
import { merge } from 'ts-deepmerge';
import { currentAccountConfigVar } from '../../apollo/cache/reactiveVars';

// relative imports
import ledgibleDefaultThemeOptions from './defaultThemeConfig';
import accountConfigSchema from './validation/account-config-schema.json';

// setup for JSON Schema validation
const ajv = new Ajv();

const validateAccount = ajv.compile(accountConfigSchema);

// Default app config fallback
const defaultAppConfig = {
  id: 'ledgible',
  appTitle: 'Ledgible',
  darkLogoUrl:
    'https://ledgible.io/wp-content/uploads/2022/05/Ledgible-Logo-Dark-Default.webp',
  defaultDocumentTitle: 'Ledgible',
  logoUrl:
    'https://ledgible.io/wp-content/uploads/2022/01/Ledgible-Logo-Original.webp',
  favicon16Href: '/favicon-16x16.png',
  favicon32Href: '/favicon-32x32.png',
  favicon96Href: '/favicon-96x96.png',
  themeId: 'ledgible',
};

const publicConfigPathUrl =
  import.meta.env.CYPRESS || (typeof window !== 'undefined' && window.Cypress)
    ? import.meta.resolve(
        `${window.Cypress?.config?.()?.devServerPublicPathRoute || ''}/config`,
      )
    : import.meta.resolve('/config');

// Cache for directory indexes to avoid repeated fetches
const directoryIndexCache = new Map();

/**
 * Find a config file by ID using directory index
 * @param {string} configType - 'app', 'theme', or 'account'
 * @param {string} configId - The ID to search for
 * @returns {Promise<string|null>} The filename if found, null otherwise
 */
const findConfigFileById = async (configType, configId) => {
  try {
    // Check cache first
    if (directoryIndexCache.has(configType)) {
      const index = directoryIndexCache.get(configType);
      return index[configId] || null;
    }

    // Fetch the directory index
    const response = await fetch(
      `${publicConfigPathUrl}/${configType}/index.json`,
    );
    if (!response.ok) {
      throw new Error(
        `Failed to load ${configType} directory index: ${response.status}`,
      );
    }
    const responseText = await response.text();
    let index;
    try {
      index = JSON.parse(responseText);
    } catch (e) {
      console.error('Failed to parse JSON:', e);
      console.error('Response text was:', responseText);
      throw e;
    }
    // Cache the index
    directoryIndexCache.set(configType, index);

    // Return the filename for the requested ID
    return index[configId] || null;
  } catch (error) {
    console.warn(
      `Error finding config file for ${configType}/${configId}:`,
      error,
    );
    return null;
  }
};

export const getAppConfig = () => window.appConfig || defaultAppConfig;
export const getAppTheme = () =>
  window.appTheme || createTheme(ledgibleDefaultThemeOptions);

// App config caching
const appConfigCache = new Map();
let currentAppConfigId = null;

/**
 * Extracts subdomain from current hostname
 * @returns {string|null} The subdomain or null if none found
 */
export const getSubdomainFromHostname = () => {
  if (typeof window === 'undefined') return null;

  // Handle localhost and IP addresses
  if (
    window.location.hostname === 'localhost' ||
    window.location.hostname.match(/^\d+\.\d+\.\d+\.\d+$/)
  ) {
    return null;
  }

  const parts = window.location.hostname.split('.');

  // Need at least 3 parts for a subdomain (subdomain.domain.tld)
  if (parts.length < 3) {
    return null;
  }

  const subdomain = parts[0];

  // Skip common non-subdomain prefixes
  if (['www', 'app', 'api', 'admin'].includes(subdomain)) {
    return null;
  }

  return subdomain;
};

/**
 * Gets app config ID from subdomain or account config
 * @param {Object} accountConfig - The account configuration object
 * @returns {string|null} The app config ID to use
 */
export const getAppConfigId = (accountConfig = {}) => {
  // Priority 1: Account config appConfigId
  if (accountConfig && accountConfig.appConfigId) {
    return accountConfig.appConfigId;
  }

  // Priority 2: Test app config ID from URL parameter (for testing)
  if (typeof window !== 'undefined' && window.Cypress) {
    const urlParams = new URLSearchParams(window.location.search);
    const testAppConfigId = urlParams.get('testAppConfigId');
    if (testAppConfigId) {
      return testAppConfigId;
    }
  }

  // Priority 3: Subdomain-based resolution
  const subdomain = getSubdomainFromHostname();
  if (subdomain) {
    return subdomain;
  }

  // Priority 4: Environment variable (for backward compatibility)
  const envAppConfigId = import.meta.env.REACT_APP_CONFIGURATION_SUBTYPE_ID;
  if (envAppConfigId) {
    return envAppConfigId;
  }

  return null;
};

export const getRemoteAppConfig = async (appConfigIdParam = null) => {
  // Cypress test mode: Check if window has a test app config set
  if (typeof window !== 'undefined' && window.Cypress && window.testAppConfig) {
    const testConfig = merge(defaultAppConfig, window.testAppConfig);
    window.appConfig = testConfig;
    return testConfig;
  }

  let appConfigId = appConfigIdParam;

  // If no appConfigId provided, try to get from various sources
  if (!appConfigId) {
    appConfigId = getAppConfigId();
  }

  // If still no appConfigId, use default
  if (!appConfigId) {
    window.appConfig = defaultAppConfig;
    currentAppConfigId = null;
    return defaultAppConfig;
  }

  // Check if we already have this app config loaded and it's the current one
  if (currentAppConfigId === appConfigId && appConfigCache.has(appConfigId)) {
    const cachedConfig = appConfigCache.get(appConfigId);
    window.appConfig = cachedConfig;
    return cachedConfig;
  }

  // Check cache for any app config with this ID
  if (appConfigCache.has(appConfigId)) {
    const cachedConfig = appConfigCache.get(appConfigId);
    window.appConfig = cachedConfig;
    currentAppConfigId = appConfigId;
    return cachedConfig;
  }

  let customAppConfig = {};

  try {
    // Find the config file by ID using directory index
    const filename = await findConfigFileById('app', appConfigId);
    if (!filename) {
      throw new Error(`No app config found with ID: ${appConfigId}`);
    }

    // Single optimized fetch with proper error handling
    const response = await fetch(`${publicConfigPathUrl}/app/${filename}`);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    customAppConfig = await response.json();
  } catch (e) {
    console.warn(
      `Failed to load app config for ${appConfigId}, using default: ${
        e?.message || 'Unknown error'
      }`,
    );
    // Fall back to default config instead of throwing
    window.appConfig = defaultAppConfig;
    return defaultAppConfig;
  }

  const updatedConfig = merge(defaultAppConfig, customAppConfig);

  // Cache the config
  appConfigCache.set(appConfigId, updatedConfig);

  // Store in window for access globally
  window.appConfig = updatedConfig;

  // Update current app config ID
  currentAppConfigId = appConfigId;

  return updatedConfig;
};

export const getRemoteAccountConfig = async ({ id: configId }) => {
  const baseAccountConfig = {
    id: 'default',
    billing: {},
    display: {},
    features: {},
    limits: {},
    enabledPageIds: [],
    setup: {},
  };

  let accountConfigLookup;

  try {
    // Find the account config file by ID using directory index
    const filename = await findConfigFileById('account', configId);
    if (!filename) {
      throw new Error(`No account config found with ID: ${configId}`);
    }
    const response = await fetch(`${publicConfigPathUrl}/account/${filename}`);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    accountConfigLookup = await response.json();
  } catch (e) {
    throw new Error(
      `Error retrieving account configuration for id ${configId} - ${
        e?.message || 'Unknown'
      }`,
    );
  }

  const valid = validateAccount(accountConfigLookup);

  if (!valid) {
    throw new Error(
      `Error validating account configuration schema - ${JSON.stringify(
        validateAccount.errors,
      )}`,
    );
  }

  const accountConfig = merge(baseAccountConfig, accountConfigLookup);
  return accountConfig;
};

// function to recursively replace matched values in object
export function replaceClientColorValuesInThemeConfig(
  configObj = {},
  colorMap = {},
) {
  const keys = Object.keys(configObj);
  const colorMapArr = Object.keys(colorMap);

  for (let i = 0; i < keys.length; i += 1) {
    const currentVal = configObj[keys[i]];
    if (typeof currentVal === 'object') {
      configObj[keys[i]] = replaceClientColorValuesInThemeConfig(
        configObj[keys[i]],
        colorMap,
      );
    }
    if (typeof currentVal === 'string' && colorMapArr.includes(currentVal)) {
      const colorKeyValue = colorMap[currentVal];
      configObj[keys[i]] = colorKeyValue;
    }
  }

  return configObj;
}

// get white label theme config based on appConfig theme id value
export const getRemoteWhiteLabelThemeConfig = async (themeId) => {
  let customThemeConfig;

  try {
    // Find the theme file by ID using directory index
    const filename = await findConfigFileById('theme', themeId);
    if (!filename) {
      throw new Error(`No theme config found with ID: ${themeId}`);
    }

    // Single optimized fetch
    const response = await fetch(`${publicConfigPathUrl}/theme/${filename}`);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    customThemeConfig = await response.json();
  } catch (e) {
    throw new Error(
      `No config found matching theme ID - ${e?.message || 'Unknown'}`,
    );
  }

  const { colorVars, ...overrides } = customThemeConfig;
  return replaceClientColorValuesInThemeConfig(overrides, colorVars);
};

export const getRemoteAppTheme = async () => {
  let themeOptions = ledgibleDefaultThemeOptions;
  const { themeId } = getAppConfig();

  if (themeId && themeId !== 'ledgible') {
    try {
      const customThemeOptions = await getRemoteWhiteLabelThemeConfig(themeId);
      themeOptions = merge(themeOptions, customThemeOptions);
    } catch (e) {
      throw new Error(
        `Unable to load custom theme options for themeId: ${themeId} - ${
          e?.message || 'Unknown'
        }`,
      );
    }
  }
  // TODO -> validate if a JSON schema gets finalized for theme structure before returning
  const appTheme = createTheme(themeOptions);
  // Store in window for access globally
  window.appTheme = appTheme;
  return appTheme;
};

// used for chart colors
export const chartColors = [
  '#6699CC',
  '#88CCEE',
  '#44AA99',
  '#117733',
  '#999933',
  '#DDCC77',
  '#661100',
  '#CC6677',
  '#AA4466',
  '#5FBD71',
  '#143945',
];

export const chartColorsAlt = [
  '#A6CEE3',
  '#FB9A99',
  '#33A02C',
  '#B2DF8A',
  '#1F78B4',
  '#E1CAF1',
  '#AC6AD8',
  '#FFD86F',
  '#89F0FF',
  '#FFF19B',
];

// ============================================================================
// Display Text Utility Functions
// ============================================================================

/**
 * Get the appropriate label for a client account based on account configuration
 * @param {Object} accountConfig - The account configuration object
 * @param {boolean} plural - Whether to return plural form (default: false)
 * @returns {string} The client label
 */
export const getClientLabel = ({ accountConfig, plural = false } = {}) => {
  if (!accountConfig?.display) return plural ? 'Clients' : 'Client';

  return plural
    ? accountConfig.display.clientLabelPlural || 'Clients'
    : accountConfig.display.clientLabel || 'Client';
};

/**
 * Get the appropriate label for an admin account based on account configuration
 * @param {Object} accountConfig - The account configuration object
 * @param {boolean} plural - Whether to return plural form (default: false)
 * @returns {string} The admin label
 */
export const getAdminLabel = ({ accountConfig, plural = false } = {}) => {
  if (!accountConfig?.display) return plural ? 'Admins' : 'Admin';

  return plural
    ? accountConfig.display.adminLabelPlural || 'Admins'
    : accountConfig.display.adminLabel || 'Admin';
};

/**
 * Check if a module is enabled in the account configuration
 * @param {Object} params - Parameters object
 * @param {Object} [params.accountConfig] - The account configuration object (optional, defaults to current)
 * @param {string} params.moduleName - The name of the module to check
 * @returns {boolean} True if the module is enabled, false otherwise
 */
export const isModuleEnabled = ({
  accountConfig = currentAccountConfigVar(),
  moduleName,
}) => accountConfig?.modules?.[moduleName] === true;

/**
 * Check if a feature is enabled within a module (module must be enabled).
 * The feature counts as enabled when its value is strictly `true`, or when it is a
 * non-empty array (e.g. `bulkOperations`).
 *
 * @param {Object} params - Parameters object
 * @param {Object} [params.accountConfig] - The account configuration object (optional, defaults to current)
 * @param {string} params.moduleName - The name of the module
 * @param {string} params.featureName - The name of the feature within the module
 * @returns {boolean} True if the feature is enabled, false otherwise
 */
export const isFeatureEnabled = ({
  accountConfig = currentAccountConfigVar(),
  moduleName,
  featureName,
}) => {
  if (accountConfig?.modules?.[moduleName] !== true) {
    return false;
  }

  const featureValue = accountConfig?.features?.[moduleName]?.[featureName];

  if (featureValue === true) {
    return true;
  }

  return Array.isArray(featureValue) && featureValue.length > 0;
};

/**
 * Check if a feature value matches a specific primitive value within a module
 * @param {Object} params - Parameters object
 * @param {Object} [params.accountConfig] - The account configuration object (optional, defaults to current)
 * @param {string} params.moduleName - The name of the module
 * @param {string} params.featureName - The name of the feature within the module
 * @param {*} params.value - The primitive value to check against (string, number, boolean)
 * @param {boolean} [params.strictModuleCheck=true] - Whether to require the module to be enabled (default: true)
 * @returns {boolean} True if the feature value matches the provided primitive value, false otherwise
 */
export const checkFeaturePrimitiveValue = ({
  accountConfig = currentAccountConfigVar(),
  moduleName,
  featureName,
  value,
  strictModuleCheck = true,
}) => {
  // If strict module check is enabled, ensure the module is enabled
  if (strictModuleCheck && accountConfig?.modules?.[moduleName] !== true) {
    return false;
  }

  // Get the feature value from the configuration
  const featureValue = accountConfig?.features?.[moduleName]?.[featureName];

  // Perform strict equality check
  return featureValue === value;
};


